import { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { getSubscriptionInfoApiV1MyAccountSubscriptionGet } from "@/backend-sdk/client/sdk.gen";
import { useAuth } from "@/contexts/auth.context";
import { semPlanoAtivo } from "@/lib/account-access-context";

type Situacao = "verificando" | "sem-assinatura" | "liberado";

/**
 * O painel só abre para o assinante que **já assinou** algum plano.
 *
 * Quem se cadastra e sai antes do checkout fica com login válido e **nenhuma assinatura**, e
 * entrava no painel como qualquer outro: as telas de dados respondiam `403 No active plan`, a
 * aba Assinatura não tinha plano atual para mostrar, e nada levava de volta à escolha do plano
 * — ela só era alcançável pelo redirecionamento logo depois do cadastro. Esta rota de layout
 * manda essa pessoa para `/register/plans`, que já sabe completar o checkout de quem está
 * logado.
 *
 * ## Quem decide é `/my-account/subscription`, e não o perfil
 *
 * O perfil (`semPlanoAtivo`) é só o pré-filtro: ele publica a assinatura `ACTIVE`/`TRIAL`, e
 * fica nulo também para quem foi **suspenso** — trial vencido ou fatura em atraso. Essa pessoa
 * não pode ir para a escolha do plano: o checkout lhe abriria uma cobrança nova ao lado da
 * que ela deve, e, no mesmo plano, só religaria `is_active` numa assinatura que continua
 * `SUSPENDED` — ela voltaria para cá num laço. Achado de code review.
 *
 * A suspensão não desliga `subscriptions.is_active` (só o `status` e o assinante), e por isso
 * `current_plan`, que sai da assinatura `is_active`, continua preenchido para o inadimplente
 * e nulo só para quem nunca concluiu um checkout. É a mesma leitura com que a aba Assinatura
 * decide mostrar a vitrine, então as duas pontas concordam. O inadimplente segue o caminho que
 * já tinha: o banner de retenção o leva à Minha Conta, onde estão as Faturas.
 *
 * A pergunta ao servidor também protege quem **acabou de assinar** com um perfil em memória
 * ainda velho: a resposta já traz o plano novo. Se ela falhar, não desvia — "não sei" não é
 * "não tem", e as rotas de dados já recusam sozinhas quem não tem plano.
 *
 * ## O que fica de fora
 *
 * **Minha Conta** e **Perfil** não passam por aqui (ver `Dashboard`). Quem não é assinante
 * (membro de time, credencial de cliente) nunca é barrado: a cobrança não é dele.
 */
const ExigePlanoAtivo = () => {
  const { user } = useAuth();
  const precisaVerificar = semPlanoAtivo(user);
  const [situacao, setSituacao] = useState<Situacao>("verificando");

  useEffect(() => {
    if (!precisaVerificar) return;
    let cancelado = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSituacao("verificando");
    getSubscriptionInfoApiV1MyAccountSubscriptionGet({ throwOnError: true })
      .then(({ data }) => {
        if (!cancelado) {
          setSituacao(data.current_plan ? "liberado" : "sem-assinatura");
        }
      })
      .catch(() => {
        if (!cancelado) setSituacao("liberado");
      });
    return () => {
      cancelado = true;
    };
  }, [precisaVerificar, user?.id]);

  if (!precisaVerificar) return <Outlet />;
  if (situacao === "verificando") return null;
  if (situacao === "sem-assinatura") {
    return <Navigate to="/register/plans" replace />;
  }
  return <Outlet />;
};

export default ExigePlanoAtivo;
